import { blobResponseHandler, downloadBlob } from '@/shared/helpers/api';
import { createLocationHubCacheListener, createSocketMessageEventListener } from '@/warehouse/signalR';
import { pickedFromStockMessageSchema, returnedFromBlockToStockMessageSchema } from '@/warehouse/signalR/types';
import { updatePickingTaskItemsByMessage, updatePickingTasksByMessage, updateVerificationItemsByMessage } from './helpers';
import { addTimestampToFileName } from '@/core/utils/datetime';
import { baseApi } from '@/warehouse/baseApi';
import { getStockLocationId } from '@/warehouse/helpers';
import { pickingActions } from './picking.state';
export const pickingApi = baseApi
    .enhanceEndpoints({ addTagTypes: ['PickingTasks', 'PickingTaskItems', 'VerificationItems', 'PickingItemBlockedItems'] })
    .injectEndpoints({
    endpoints: (builder) => ({
        getPickingTasks: builder.query({
            query: (params) => ({
                url: `stock/${getStockLocationId()}/picking/tasks`,
                method: 'POST',
                body: params,
            }),
            // Should not be used... socket messages updates
            // providesTags: (result) => providesList(result, 'PickingTasks'),
            onQueryStarted: async (queryArgument, { dispatch, queryFulfilled }) => {
                const { article, isPicked } = queryArgument;
                try {
                    const result = await queryFulfilled;
                    // Counters should not be changed if tasks are filtered by search term.
                    if (article)
                        return;
                    if (isPicked)
                        dispatch(pickingActions.setPickedTaskCount(result.data?.length));
                    else
                        dispatch(pickingActions.setNotPickedTaskCount(result.data?.length));
                }
                catch {
                    //
                }
            },
            onCacheEntryAdded: createLocationHubCacheListener(({ updateCachedData, getCacheEntry, dispatch }, { isPicked }) => {
                const eventListener = createSocketMessageEventListener({
                    endpointName: getCacheEntry().endpointName,
                    validationSchema: pickedFromStockMessageSchema,
                    listener: ({ message }) => {
                        updateCachedData((draft) => {
                            const itemCount = draft.length;
                            // TODO: CHECK if it is working! (isPicked). Mb it should be passed via queryArgument param's prop?
                            updatePickingTasksByMessage(draft, message, isPicked);
                            if (itemCount !== draft.length) {
                                dispatch(isPicked
                                    ? pickingActions.setPickedTaskCount(draft.length)
                                    : pickingActions.setNotPickedTaskCount(draft.length));
                            }
                        });
                    },
                });
                return {
                    PickedFromStock: eventListener,
                    PutBackToStock: eventListener,
                    // This two messages only affect `blockedQuantity`, that is currently not shown in Tasks list.
                    // But if `blockedQuantity` field will be added to tasks list, socket messages updates will be working.
                    BlockedOnPicking: eventListener,
                    ReturnedFromBlockToPicking: eventListener,
                };
            }),
        }),
        getVerificationItems: builder.query({
            query: () => `stock/${getStockLocationId()}/picking/items`,
            onQueryStarted: async (request, { dispatch, queryFulfilled }) => {
                try {
                    const result = await queryFulfilled;
                    dispatch(pickingActions.setVerificationItemCount(result.data?.length));
                }
                catch {
                    //
                }
            },
            onCacheEntryAdded: createLocationHubCacheListener(({ updateCachedData, getCacheEntry, dispatch }) => {
                const eventListener = createSocketMessageEventListener({
                    endpointName: getCacheEntry().endpointName,
                    validationSchema: pickedFromStockMessageSchema,
                    listener: ({ message }) => {
                        updateCachedData((draft) => {
                            const itemCount = draft.length;
                            updateVerificationItemsByMessage(draft, message);
                            if (itemCount !== draft.length) {
                                dispatch(pickingActions.setVerificationItemCount(draft.length));
                            }
                        });
                    },
                });
                return {
                    PickedFromStock: eventListener,
                    PutBackToStock: eventListener,
                    BlockedOnPicking: eventListener,
                    ReturnedFromBlockToPicking: eventListener,
                };
            }),
        }),
        getPickingTaskItems: builder.query({
            query: ({ taskId }) => `stock/${getStockLocationId()}/picking/${taskId}/items`,
            onCacheEntryAdded: createLocationHubCacheListener(({ updateCachedData, getCacheEntry }) => {
                const { endpointName } = getCacheEntry();
                const eventListener = createSocketMessageEventListener({
                    endpointName,
                    validationSchema: pickedFromStockMessageSchema,
                    listener: ({ message }) => {
                        updateCachedData((draft) => {
                            updatePickingTaskItemsByMessage(draft, message);
                        });
                    },
                });
                const returnedFromBlockToStockEventListener = createSocketMessageEventListener({
                    endpointName,
                    validationSchema: returnedFromBlockToStockMessageSchema,
                    listener: ({ message }) => {
                        updateCachedData((draft) => {
                            // // TODO: to delete
                            // console.log(`${endpointName}, onCacheEntryAdded in '${eventName}', draft:`, current(draft));
                            // console.log(`${endpointName}, onCacheEntryAdded in '${eventName}', message:`, message);
                            updatePickingTaskItemsByMessage(draft, message);
                        });
                    },
                });
                return {
                    PickedFromStock: eventListener,
                    PutBackToStock: eventListener,
                    BlockedOnPicking: eventListener,
                    ReturnedFromBlockToPicking: eventListener,
                    // TODO: check if `eventListener` may be used.
                    ReturnedFromBlockToStock: returnedFromBlockToStockEventListener,
                };
            }),
        }),
        pickupPickingItem: builder.mutation({
            query: ({ id, quantity }) => ({
                method: 'POST',
                url: `stock/${getStockLocationId()}/picking/items/${id}/pickup`,
                body: { quantity },
            }),
            // Invalidation is handled by socket messages.
        }),
        getPickingItemBlockedItems: builder.query({
            query: ({ pickingItemId }) => `stock/${getStockLocationId()}/picking/items/${pickingItemId}/blocked`,
            // `refetchOnMountOrArgChange` is used instead, since to know when to invalidate, socket messages must be
            // checked for each item in a list.
            // providesTags: (result) => providesList(result, 'PickingItemBlockedItems'),
        }),
        blockPickingItem: builder.mutation({
            query: ({ id, quantity, reason }) => ({
                method: 'POST',
                url: `stock/${getStockLocationId()}/picking/items/${id}/block`,
                body: { quantity, reason },
            }),
            // Invalidation is handled by socket messages.
        }),
        unblockPickingItem: builder.mutation({
            query: ({ revokeItems }) => ({
                method: 'DELETE',
                url: `stock/${getStockLocationId()}/picking/items/blocked`,
                body: revokeItems.map((item) => item.id),
            }),
            // `refetchOnMountOrArgChange` is used instead, since to know when to invalidate, socket messages must be
            // checked for each item in a list.
            // invalidatesTags: (result, error, { revokeItems }) => (error ? [] : [{ type: 'PickingItemBlockedItems', id: 'LIST' }]),
        }),
        putoutPickingItem: builder.mutation({
            query: ({ pickingItemId }) => ({
                method: 'POST',
                url: `stock/${getStockLocationId()}/picking/items/${pickingItemId}/putout`,
            }),
        }),
        exportPickingTaskToExcel: builder.mutation({
            queryFn: async ({ taskId }, api, extraOptions, baseQuery) => downloadBlob({
                queryResult: await baseQuery({
                    method: 'GET',
                    cache: 'no-cache',
                    url: `stock/${getStockLocationId()}/picking/${taskId}/items/xlsx`,
                    responseHandler: blobResponseHandler,
                }),
                fileName: `stockPickingTask_${taskId}.xlsx`,
            }),
        }),
        exportPickingVerificationToExcel: builder.mutation({
            queryFn: async (arg, api, extraOptions, baseQuery) => downloadBlob({
                queryResult: await baseQuery({
                    method: 'GET',
                    cache: 'no-cache',
                    url: `stock/${getStockLocationId()}/picking/items/xlsx`,
                    responseHandler: blobResponseHandler,
                }),
                fileName: `stockPickingVerification_${addTimestampToFileName()}.xlsx`,
            }),
        }),
    }),
});
export const { useGetPickingTasksQuery, useGetVerificationItemsQuery, useGetPickingTaskItemsQuery, useExportPickingTaskToExcelMutation, useExportPickingVerificationToExcelMutation, useGetPickingItemBlockedItemsQuery, } = pickingApi;
